import { useCallback, useEffect, useRef, useState } from 'react';

import {
  RiPauseCircleLine,
  RiPlayCircleLine,
  RiResetLeftLine,
} from '@remixicon/react';
import classNames from 'classnames';

import '@spotify-internal/csr-player';
import type {
  CsrPlayerElement,
  MetricEvent,
} from '@spotify-internal/csr-player';

import { PlaybackControls, type PlaybackDefaults } from './PlaybackControls';
import { useReplayFocusGuard } from './useReplayFocusGuard';

import styles from './RecordingPlayer.module.css';

const PLAY_OVERLAY_ICON_SIZE = 80;

type RecordingPlayerVariant = 'full' | 'simplified';

type RecordingPlayerProps = {
  src: string;
  autoplay?: boolean;
  variant?: RecordingPlayerVariant;
  metricEvents?: MetricEvent[];
  initialPageUrl?: string;
  playbackDefaults?: PlaybackDefaults;
  onPlayerReady?: (player: CsrPlayerElement) => void;
  onMetricEventsReady?: (events: MetricEvent[]) => void;
  onEventMarkerClick?: (timestamp: number) => void;
  onError?: (err: Error) => void;
};

export const RecordingPlayer = ({
  src,
  autoplay = true,
  variant = 'full',
  metricEvents,
  initialPageUrl,
  playbackDefaults,
  onPlayerReady,
  onMetricEventsReady,
  onEventMarkerClick,
  onError,
}: RecordingPlayerProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<CsrPlayerElement | null>(null);
  const [isPaused, setIsPaused] = useState(true);
  const [isEnded, setIsEnded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [internalEvents, setInternalEvents] = useState<MetricEvent[]>([]);

  const effectiveMetricEvents = metricEvents ?? internalEvents;

  useReplayFocusGuard(player);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Ref avoids re-subscribing event listeners when parents pass inline
  // callbacks, which would risk missing the one-shot `loadeddata` event.
  const callbacksRef = useRef({
    onPlayerReady,
    onMetricEventsReady,
    onError,
  });
  callbacksRef.current = { onPlayerReady, onMetricEventsReady, onError };

  const playerRefCallback = useCallback((el: CsrPlayerElement | null) => {
    if (!el) {
      setPlayer(null);
      return;
    }
    // Wait for the custom element upgrade so the element has its full API.
    customElements.whenDefined('csr-player').then(() => setPlayer(el));
  }, []);

  useEffect(() => {
    if (!player) return undefined;

    const handleLoaded = () => {
      callbacksRef.current.onPlayerReady?.(player);
      const events = player.events ?? [];
      setInternalEvents(events);
      callbacksRef.current.onMetricEventsReady?.(events);
      if (autoplay) {
        player.play();
      }
    };

    const handleError = (event: Event) => {
      const errorEvent = event as ErrorEvent;
      const err =
        errorEvent.error instanceof Error
          ? errorEvent.error
          : new Error(errorEvent.message || 'Failed to load recording');
      callbacksRef.current.onError?.(err);
    };

    const handlePlay = () => {
      setIsPaused(false);
      setIsEnded(false);
    };
    const handlePause = () => setIsPaused(true);
    const handleEnded = () => setIsEnded(true);

    player.addEventListener('loadeddata', handleLoaded);
    player.addEventListener('error', handleError);
    player.addEventListener('play', handlePlay);
    player.addEventListener('pause', handlePause);
    player.addEventListener('ended', handleEnded);

    return () => {
      player.removeEventListener('loadeddata', handleLoaded);
      player.removeEventListener('error', handleError);
      player.removeEventListener('play', handlePlay);
      player.removeEventListener('pause', handlePause);
      player.removeEventListener('ended', handleEnded);
    };
  }, [player, autoplay]);

  const handleTogglePlay = () => {
    if (!player) return;
    if (isPaused) {
      player.play();
    } else {
      player.pause();
    }
  };

  const handleToggleFullscreen = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;

    const fullscreenRequest = document.fullscreenElement
      ? document.exitFullscreen()
      : root.requestFullscreen();

    void fullscreenRequest.catch(() => undefined);
  }, []);

  const isSimplified = variant === 'simplified';

  const controls = player ? (
    <PlaybackControls
      player={player}
      metricEvents={effectiveMetricEvents}
      variant={isFullscreen ? 'simplified' : variant}
      initialPageUrl={initialPageUrl}
      playbackDefaults={playbackDefaults}
      onEventMarkerClick={onEventMarkerClick}
      fullscreenControl={
        document.fullscreenEnabled
          ? {
              isFullscreen,
              onToggle: handleToggleFullscreen,
            }
          : undefined
      }
      menuContainer={isFullscreen ? (rootRef.current ?? undefined) : undefined}
    />
  ) : null;

  let OverlayIcon = RiPauseCircleLine;
  let overlayLabel = 'Pause';
  let overlayAction: 'pause' | 'play' | 'replay' = 'pause';

  if (isEnded) {
    OverlayIcon = RiResetLeftLine;
    overlayLabel = 'Replay';
    overlayAction = 'replay';
  } else if (isPaused) {
    OverlayIcon = RiPlayCircleLine;
    overlayLabel = 'Play';
    overlayAction = 'play';
  }

  return (
    <div
      ref={rootRef}
      className={classNames(styles.root, {
        [styles.rootSimplified]: isSimplified,
      })}
      data-csr-block
      data-track-scope="region.player"
    >
      <div className={styles.playerContainer}>
        <csr-player
          ref={playerRefCallback}
          src={src}
          data-testid="csr-player"
        />
        {player && (
          <button
            type="button"
            className={classNames(styles.playOverlay, {
              [styles.playOverlayPaused]: isPaused,
            })}
            onClick={handleTogglePlay}
            aria-label={overlayLabel}
            data-track-id="button.playback"
            data-track-action={overlayAction}
          >
            <OverlayIcon size={PLAY_OVERLAY_ICON_SIZE} aria-hidden />
          </button>
        )}
        {player && isSimplified && (
          <div
            className={classNames(styles.controlsOverlay, {
              [styles.controlsOverlayPaused]: isPaused,
            })}
          >
            {controls}
          </div>
        )}
      </div>
      {player && !isSimplified && metricEvents && (
        <div
          className={classNames({
            [styles.controlsOverlay]: isFullscreen,
            [styles.controlsOverlayPaused]: isFullscreen && isPaused,
          })}
        >
          {controls}
        </div>
      )}
    </div>
  );
};
